import { zodResolver } from '@hookform/resolvers/zod'
import {
	type BackofficeAuthResetPasswordData,
	backofficeAuthResetPassword
} from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { InputPassword } from '@repo/ui/commons/input-password'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Trans, useTranslation } from '@/lib/i18n'
import { fetchFreshSession } from '@/lib/query-options'
import { SetPasswordSchema } from './-lib/form-schemas'

export const Route = createFileRoute('/(auth)/reset-password')({
	validateSearch: z.object({
		token: z.string().optional()
	}),
	component: AdminResetPassword
})

function AdminResetPassword() {
	const { t } = useTranslation()
	const { token } = Route.useSearch()

	if (!token) {
		return (
			<AuthCard>
				<AuthCardHeader
					description={t('auth:reset_password.invalid_token')}
					title={t('auth:reset_password.title')}
				/>
				<AuthCardBody>
					<div className='flex flex-col items-center'>
						<ButtonLink to='/forgot-password' variant='link'>
							{t('auth:reset_password.request_new_link')}
						</ButtonLink>
						<ButtonLink to='/login' variant='link'>
							{t('auth:back_to_login')}
						</ButtonLink>
					</div>
				</AuthCardBody>
			</AuthCard>
		)
	}

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:reset_password.description')}
				title={t('auth:reset_password.title')}
			/>
			<AuthCardBody>
				<ResetPasswordForm token={token} />
				<p className='text-center text-muted-foreground text-sm'>
					<Trans
						components={{
							requestLink: (
								<Link
									className='underline'
									to='/forgot-password'
								>
									placeholder
								</Link>
							)
						}}
						i18nKey='auth:reset_password.request_another'
					/>
				</p>
			</AuthCardBody>
		</AuthCard>
	)
}

function ResetPasswordForm(props: { token: string }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const form = useForm<z.infer<typeof SetPasswordSchema>>({
		resolver: zodResolver(SetPasswordSchema),
		defaultValues: { newPassword: '', confirmPassword: '' }
	})

	const resetPassword = useMutation({
		mutationFn: (body: BackofficeAuthResetPasswordData['body']) =>
			backofficeAuthResetPassword({ body }),
		onSuccess: async () => {
			// The reset link signs the admin in: revalidate the cached session
			// before navigating so the private guard reads the fresh state.
			await fetchFreshSession(queryClient)
			await navigate({ to: '/' })
		}
	})

	return (
		<form
			onSubmit={form.handleSubmit((data) =>
				resetPassword.mutate({
					type: 'magic-link',
					value: props.token,
					newPassword: data.newPassword
				})
			)}
		>
			<FieldGroup>
				{resetPassword.isError && (
					<AuthErrorAlert
						onClose={() => resetPassword.reset()}
						title={formatError(resetPassword.error)}
					/>
				)}
				<Controller
					control={form.control}
					name='newPassword'
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor={field.name}>
								{t('common:new_password')}
							</FieldLabel>
							<InputPassword
								{...field}
								aria-invalid={fieldState.invalid}
								id={field.name}
							/>
							{fieldState.invalid && (
								<FieldError errors={[fieldState.error]} />
							)}
						</Field>
					)}
				/>
				<Controller
					control={form.control}
					name='confirmPassword'
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor={field.name}>
								{t('auth:confirm_password')}
							</FieldLabel>
							<InputPassword
								{...field}
								aria-invalid={fieldState.invalid}
								id={field.name}
							/>
							{fieldState.invalid && (
								<FieldError errors={[fieldState.error]} />
							)}
						</Field>
					)}
				/>
				<Field>
					<Button disabled={resetPassword.isPending} type='submit'>
						{resetPassword.isPending ? (
							<Spinner />
						) : (
							t('auth:reset_password.submit')
						)}
					</Button>
				</Field>
			</FieldGroup>
		</form>
	)
}
